import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { TmDetailedError } from '@treasury/domain/shared';
import {
    CreditorsService,
    WireCreditor,
    WireIsoService,
    WiresService,
    WireTemplate,
    WireType,
} from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels } from '@treasury/domain/wires';
import { Wire } from '@treasury/domain/wires/wire.entity';
import { TmContainer } from '@treasury/presentation';
import { camelCaseToTitle } from '@treasury/utils';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/wire-confirm-step';
import '../partials/wire-create-step';
import '../partials/wire-review-step';
import '../partials/wire-select-step';
import type { WireAction, WireWorkflowParameters } from '../types/wire-workflow-parameters.type';

enum Step {
    Select,
    Create,
    CreateCreditor,
    Review,
    ReviewCreditor,
    Confirm,
}

export const tagName = 'wires-workflow-container';
@customElement(tagName)
export class WiresWorkflowContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private wire = new Wire();

    @state()
    private wireTemplate = new WireTemplate();

    @state()
    private wireError?: TmDetailedError;

    @state()
    private wireType = WireType.Domestic;

    @state()
    private creditor: WireCreditor = new WireCreditor();

    @state()
    private wireAction = '' as WireAction;

    @state()
    private wireId = '';

    @state()
    private activeStep = Step.Select;

    private bottomSheetOpen = false;

    @state()
    private saveTemplate = false;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    protected render() {
        return html`<div class="bg-page h-dvh">
            ${this.loading ? this.renderBlockingLoader() : this.renderStep()}
        </div>`;
    }

    protected async firstUpdated() {
        await this.setParams();
        const validDefaultFrequency = await this.wireService.getValidDefaultFrequency();
        this.wire.frequency = validDefaultFrequency;
        this.activeStep = this.wireAction === 'create' ? Step.Create : Step.Select;
        this.wireIsoLabels = await this.wireIsoService.getLabels();
        this.loading = false;
    }

    private async setParams() {
        const { params } = await this.navService.getRouteData<WireWorkflowParameters>();
        const { type, action, id } = params;
        this.wireType = type === WireType.Domestic ? WireType.Domestic : WireType.International;
        this.wireAction = action;
        this.wireId = id;
    }

    private renderStep() {
        switch (this.activeStep) {
            case Step.Select:
                this.headerService.configure({
                    title: 'Select Wire Type',
                    backAction: () => this.navService.navigate('/wires'),
                    enableFdicSign: false,
                });
                return html`<wire-select-step
                    @create=${() => {
                        this.activeStep = Step.Create;
                    }}
                ></wire-select-step>`;
            case Step.Create:
                this.headerService.configure({
                    title: 'Create Wire',
                    subtitle: camelCaseToTitle(this.wireType),
                    backAction: () => {
                        this.navService.navigate('/wires/domestic/select/new/usd');
                    },
                    enableFdicSign: true,
                });
                return html`<wire-create-step
                    .wire=${this.wire}
                    .wireTemplate=${this.wireTemplate}
                    .wireType=${this.wireType}
                    .wireId=${this.wireId !== 'new' ? parseInt(this.wireId) : undefined}
                    @review=${(e: CustomEvent) => {
                        this.wire = e.detail;
                        this.activeStep = Step.Review;
                    }}
                    @create-creditor=${() => {
                        this.activeStep = Step.CreateCreditor;
                    }}
                ></wire-create-step>`;
            case Step.CreateCreditor:
                this.headerService.configure({
                    title: `Create ${this.wireIsoLabels.creditor}`,
                    backAction: () => {
                        this.activeStep = Step.Create;
                    },
                });
                this.creditor.isInternational = this.wireType === WireType.International;
                return html`<creditor-create-step
                    .creditor=${this.creditor}
                    @review=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.activeStep = Step.ReviewCreditor;
                    }}
                ></creditor-create-step>`;
            case Step.Review:
                this.headerService.configure({
                    title: 'Review Wire',
                    backAction: () => {
                        this.activeStep = Step.Create;
                    },
                    enableFdicSign: true,
                });
                return html`<wire-review-step
                    class="bg-primary"
                    .wire=${this.wire}
                    .wireIsoLabels=${this.wireIsoLabels}
                    @confirm=${({ detail }: CustomEvent) => {
                        this.saveTemplate = detail.saveTemplate;
                        this.wireTemplate = detail.wireTemplate;
                        this.createWire();
                    }}
                ></wire-review-step>`;
            case Step.ReviewCreditor:
                this.headerService.configure({
                    title: `${this.wireIsoLabels.creditor} Detail`,
                    backAction: () => {
                        this.activeStep = Step.CreateCreditor;
                    },
                });
                return html`<creditor-review-step
                    class="bg-primary"
                    .creditor=${this.creditor}
                    .workflow=${'wire'}
                    @continue=${({ detail }: CustomEvent) => {
                        this.wire.beneficiary = detail;
                        this.wire.beneficiary.isFreeForm = true;
                        this.activeStep = Step.Create;
                    }}
                    @continue-save=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.createCreditor();
                        this.wire.beneficiary = detail;
                        this.activeStep = Step.Create;
                    }}
                ></creditor-review-step>`;
            case Step.Confirm:
                this.headerService.configure({ title: 'Wire Confirmation' });
                return html`<wire-confirm-step
                    .wire=${this.wire}
                    .wireTemplateName=${this.wireTemplate.name}
                    @create-wire=${() => {
                        this.navService.navigate(`/wires/domestic/select/new/usd`);
                    }}
                    @view-wires=${() => {
                        this.navService.navigate('/wires');
                    }}
                ></wire-confirm-step>`;
            default:
                throw new Error(`Cannot render step. Invalid step: "${this.activeStep}".`);
        }
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return nothing;
    }

    private async createCreditor() {
        this.loading = true;
        this.wireError = undefined;
        try {
            const response = await this.creditorsService.create(this.creditor);
            this.wire.beneficiary = response as WireCreditor;
            this.activeStep = Step.Create;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.wireError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }

    private async createWire() {
        this.loading = true;
        this.wireError = undefined;
        try {
            const response = await this.wireService.createWirePayment(this.wire);
            this.wire = new Wire(response);
            if (this.saveTemplate) {
                try {
                    await this.wireService.wiresCreateTemplate(this.wireTemplate);
                } catch {
                    this.wireTemplate.name = '';
                    this.notificationService.renderError('Error saving wire template');
                }
            }
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.wireError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WiresWorkflowContainer;
    }
}
